import * as React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

import { WHOLESALE_PRICE_SUFFIX } from "../../routes";
import Paper from "@mui/material/Paper";

import { useEffect, useState } from "react";
import CheckboxGroup from "./CheckboxGroup/CheckboxGroup";
import useQueryParams, {
  ACCOUNT_PARAM,
  COUNTRY_PARAM,
  TERRITORY_PARAM,
} from "../../hooks/useQueryParams";
import { useLocation } from "react-router-dom";

import { COUNTRIES, TERRITORY_MENU_DATA } from "./config/Filters";
import { accountMenuData, FireNav } from "./LeftNav.interface";
import { ROUTE_TO_FE_VALUE } from "../../constants/app.constants";
import useClient from "../../hooks/useClient";

const countryMenuData = [{ label: "MEXICO", value: "MEXICO" }];
const getDefaultValue = (
  value: { label: string; value: string }[],
  accessor = 0,
) => [value[accessor].value.toLowerCase()];

export const DEFAULT_CHECKS = {
  [TERRITORY_PARAM]: new Set<string>(getDefaultValue(TERRITORY_MENU_DATA)),
  [COUNTRY_PARAM]: new Set<string>(getDefaultValue(countryMenuData)),
  [ACCOUNT_PARAM]: new Set<string>(),
};

export type Checks = {
  [TERRITORY_PARAM]: Set<string>;
  [COUNTRY_PARAM]: Set<string>;
  [ACCOUNT_PARAM]: Set<string>;
};

export default function ShipmentSubmitFilters() {
  const [checks, setIsChecks] = useState<Checks>({ ...DEFAULT_CHECKS });
  const [disabledCheckboxes, setDisabledCheckboxes] = useState<Set<string>>(
    new Set(),
  );
  const { role } = useClient();
  const { pathname } = useLocation();
  const { clearParams, queryParams, iterateOverParameters, search } =
    useQueryParams();

  const [currentLocation, setCurrentLocation] = useState("");
  const [channel, ...rest] = pathname.split("/")[1].split("-");
  if (rest[rest.length-1] === WHOLESALE_PRICE_SUFFIX) {
    rest.pop();  // makes existing UNITS flow business models validation work with the wholesale price flow
  }
  const businessModel = rest.join("-");
  const [changingNavs, setChangingNavs] = useState({
    [COUNTRY_PARAM]: [...countryMenuData],
    [ACCOUNT_PARAM]: [...accountMenuData],
  });
  const hideAccountFiltersMenu: boolean =
    changingNavs[ACCOUNT_PARAM].length === 0;

  const bypassDisabledCheckboxes = (
    checked: Checks,
    disabledChecks: Set<string>,
  ) => {
    const territories = TERRITORY_MENU_DATA.filter(
      (territory) => !disabledChecks.has(territory.value.toUpperCase()),
    );
    const territory = territories[0].value;
    const countries = territory && getCountriesForTerritory(territory);
    const country = typeof countries[0] === "object" ? countries[0].value : "";
    checked[TERRITORY_PARAM] = new Set([territory.toLowerCase()]);
    checked[COUNTRY_PARAM] = new Set([country.toLowerCase()]);
    const accounts = country && getAccounts(country);
    if (accounts && accounts.length) {
      checked[ACCOUNT_PARAM] = new Set([accounts[0].value.toLowerCase()]);
    }
    setDisabledCheckboxes(disabledChecks);
    setIsChecks(checked);
    iterateOverParameters(checked);
    setChangingNavs({
      ...changingNavs,
      [COUNTRY_PARAM]: countries || [],
      [ACCOUNT_PARAM]: accounts || [],
    });
  };
  useEffect(() => {
    const updatedLocation = pathname.split("/")[1];
    if (updatedLocation !== currentLocation) {
      const disabledCountries = TERRITORY_MENU_DATA.flatMap((territory) => {
        return territory.countries.filter((country) => {
          const hasChannel =
            COUNTRIES[country.toUpperCase()][channel.toUpperCase()];
          if (!hasChannel) return true;
          const hasBusinessModel = hasChannel[ROUTE_TO_FE_VALUE[businessModel]];
          if (!hasBusinessModel) return true;
        });
      }).map((val) => val.toUpperCase());

      const disabledTerritories = TERRITORY_MENU_DATA.filter((territory) => {
        return territory.countries.every((country) => {
          return disabledCountries.includes(country.toUpperCase());
        });
      }).map((val) => val.value.toUpperCase());
      const updatedDisabledCheckboxes = new Set([
        ...disabledCountries,
        ...disabledTerritories,
      ]);

      if (
        !search.length &&
        !role.toLowerCase().includes("admin") &&
        role !== "Unknown"
      ) {
        const territory = TERRITORY_MENU_DATA.find(
          (territory) => territory.label === role.toUpperCase(),
        );
        if (territory) {
          if (updatedDisabledCheckboxes.has(territory.value.toUpperCase())) {
            bypassDisabledCheckboxes(
              { ...DEFAULT_CHECKS },
              updatedDisabledCheckboxes,
            );
          } else {
            const countries = getCountriesForTerritory(territory.value);
            let country = countries[0].value;
            if (updatedDisabledCheckboxes.has(country.toUpperCase())) {
              const enabledCountries = countries.filter(
                (val) =>
                  !updatedDisabledCheckboxes.has(val.value.toUpperCase()),
              );
              country = enabledCountries[0].value;
            }
            const accounts = country && getAccounts(country);
            const checked = { ...DEFAULT_CHECKS };
            checked[COUNTRY_PARAM] = new Set([country.toLowerCase()]);
            checked[TERRITORY_PARAM] = new Set([role.toLowerCase()]);
            if (accounts.length > 0 && typeof accounts === "object") {
              checked[ACCOUNT_PARAM] = new Set([
                accounts[0].value.toLowerCase(),
              ]);
            }
            setDisabledCheckboxes(updatedDisabledCheckboxes);
            iterateOverParameters(checked);
            setIsChecks(checked);
            setChangingNavs({
              ...changingNavs,
              [COUNTRY_PARAM]: countries || [],
              [ACCOUNT_PARAM]: accounts || [],
            });
          }
        }
      } else {
        const checked = {
          [ACCOUNT_PARAM]: new Set<string>(),
          [COUNTRY_PARAM]: new Set<string>(),
          [TERRITORY_PARAM]: new Set<string>(),
        };
        const territory =
          queryParams.get(TERRITORY_PARAM) || TERRITORY_MENU_DATA[0].value;

        if (updatedDisabledCheckboxes.has(territory.toUpperCase())) {
          bypassDisabledCheckboxes(checked, updatedDisabledCheckboxes);
        } else {
          const countries = territory && getCountriesForTerritory(territory);

          let country = queryParams.get(COUNTRY_PARAM) || "";
          if (!country) {
            const updatedTerritory = TERRITORY_MENU_DATA.find(
              (val) => val.value === territory.toUpperCase(),
            );
            country = updatedTerritory ? updatedTerritory.countries[0] : "";
          }
          if (
            updatedDisabledCheckboxes.has(country.toUpperCase()) &&
            typeof countries !== "string"
          ) {
            const enabledCountries = countries.filter(
              (val) => !updatedDisabledCheckboxes.has(val.value.toUpperCase()),
            );
            country = enabledCountries[0].value;
          }
          const accountParam = queryParams.get(ACCOUNT_PARAM) || "";
          const accounts = country && getAccounts(country);
          checked[TERRITORY_PARAM] = new Set([territory.toLowerCase()]);
          checked[COUNTRY_PARAM] = new Set([country.toLowerCase()]);

          if (accounts && accounts.length) {
            const account = accountParam.length
              ? accountParam
              : accounts[0].value;
            checked[ACCOUNT_PARAM] = new Set([account.toLowerCase()]);
          }
          setDisabledCheckboxes(updatedDisabledCheckboxes);
          setIsChecks(checked);
          iterateOverParameters(checked);
          setChangingNavs({
            ...changingNavs,
            [COUNTRY_PARAM]: countries || [],
            [ACCOUNT_PARAM]: accounts || [],
          });
        }
      }
      setCurrentLocation(updatedLocation);
    }
  }, [pathname]);

  const getCountriesForTerritory = (territoryValue: string) => {
    const territory = TERRITORY_MENU_DATA.find(
      (val) => val.value === territoryValue.toUpperCase(),
    ) as { countries: string[] };
    return territory?.countries.map((val) => ({
      label: val,
      value: val,
    }));
  };

  const getAccounts = (country: string): { value: string; label: string }[] => {
    // Because countries w/o this channel /business model have been disabled, this should work
    const countryChannel =
      COUNTRIES[country.toUpperCase()][channel.toUpperCase()];
    if (!countryChannel) return [];
    const values = countryChannel[ROUTE_TO_FE_VALUE[businessModel]];
    return values && typeof values === "object"
      ? Object.keys(values).map((account) => ({
          value: values[account] as unknown as string,
          label: account,
        }))
      : ([] as { value: string; label: string }[]);
  };

  const onChange = (parameterName: string) => (checkboxId: string) => {
    // if parameterName is TERRITORY_PARAM, clear all params except incoming parameter
    const territoryValue = checkboxId.toUpperCase();
    if (parameterName === TERRITORY_PARAM) {
      const defaultChecks = {
        [TERRITORY_PARAM]: new Set<string>(),
        [COUNTRY_PARAM]: new Set<string>(),
        [ACCOUNT_PARAM]: new Set<string>(),
      };
      const checked = checks[parameterName].has(checkboxId)
        ? defaultChecks
        : { ...defaultChecks, [parameterName]: new Set<string>([checkboxId]) };

      if (checked[parameterName].size) {
        iterateOverParameters(checked);
        // this should always find the country since it's being passed in from the TERRITORY_MENU_DATA
        const countries = getCountriesForTerritory(territoryValue);
        setChangingNavs({
          ...changingNavs,
          [COUNTRY_PARAM]: countries,
          [ACCOUNT_PARAM]: [],
        });
      } else {
        clearParams();
      }
      setIsChecks(checked);
    }

    // if parameterName is COUNTRY_PARAM, remove other countries and set the incoming country
    if (parameterName === COUNTRY_PARAM) {
      const defaultChecks = {
        ...checks,
        [COUNTRY_PARAM]: new Set<string>(),
        [ACCOUNT_PARAM]: new Set<string>(),
      };
      const checked = checks[parameterName].has(checkboxId)
        ? defaultChecks
        : { ...defaultChecks, [parameterName]: new Set<string>([checkboxId]) };

      const accounts = getAccounts(checkboxId);
      if (accounts && accounts.length) {
        checked[ACCOUNT_PARAM] = new Set([accounts[0].value.toLowerCase()]);
      }
      iterateOverParameters(checked);
      setIsChecks(checked);
      setChangingNavs({
        ...changingNavs,
        [ACCOUNT_PARAM]: accounts,
      });
    }

    // handle accounts being checked
    if (parameterName === ACCOUNT_PARAM) {
      const accounts = new Set([checkboxId]);
      const newChecks = {
        ...checks,
        [ACCOUNT_PARAM]: accounts,
      };
      iterateOverParameters(newChecks);
      setIsChecks(newChecks);
    }
  };
  return (
    <Box minHeight="100vh" sx={{ display: "flex", borderRadius: 0 }}>
      <Paper
        style={{
          backgroundColor: "rgb(30, 30, 47)",
          color: "#fff",
          width: 250,
        }}
      >
        <FireNav disablePadding>
          <Box marginTop="80px">
            <CheckboxGroup
              disabledCheckboxes={disabledCheckboxes}
              checkboxes={TERRITORY_MENU_DATA}
              label={"Territory"}
              onChange={onChange(TERRITORY_PARAM)}
              checked={checks[TERRITORY_PARAM]}
            />
            <Divider />
            <CheckboxGroup
              disabledCheckboxes={disabledCheckboxes}
              checkboxes={changingNavs[COUNTRY_PARAM]}
              label={"Country"}
              onChange={onChange(COUNTRY_PARAM)}
              checked={checks[COUNTRY_PARAM]}
            />
            {!hideAccountFiltersMenu && (
              <>
                <Divider />
                <CheckboxGroup
                  checkboxes={changingNavs[ACCOUNT_PARAM]}
                  label={"Account"}
                  onChange={onChange(ACCOUNT_PARAM)}
                  checked={checks[ACCOUNT_PARAM]}
                />
              </>
            )}
            <Divider />
          </Box>
        </FireNav>
      </Paper>
    </Box>
  );
}
